import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ShoppingCart, Search } from 'lucide-react';
import { useQuote } from '@/context/QuoteContext';
import { useState, useEffect } from 'react';
import { GlobalSearchModal } from '@/components/GlobalSearchModal';
import './Navbar.css';



export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const { count, openDrawer } = useQuote();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track location changes to close menu
  const [prevLocation, setPrevLocation] = useState(location.pathname);
  if (location.pathname !== prevLocation) {
    setPrevLocation(location.pathname);
    setIsOpen(false);
  }

  const navItems = [
    { name: 'Ana Sayfa', path: '/' },
    { 
      name: 'Hakkımızda', 
      isDropdown: true,
      children: [
        { name: 'Kurumsal', path: '/hakkimizda' },
        { name: 'Markalar', path: '/markalar' },
        { name: 'Referanslar', path: '/referanslar' },
      ]
    },
    { name: 'Creavit', path: '/urunler' },
    { name: 'Duratiles', path: '/duratiles' },
    { name: 'Kalekim', path: '/kalekim' },
    { name: 'QUA', path: '/qua' },
    { name: 'VitraFix', path: '/vitrafix' },
    { name: 'İletişim', path: '/iletisim' },
  ];

  return (
    <header className={`navbar flex items-center ${scrolled ? 'scrolled glass' : ''}`}>
      <div className="container flex items-center justify-between" style={{ width: '100%', height: '100%', position: 'relative', alignItems: 'center' }}>
        <Link to="/" className="navbar-logo" style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <img
            src="/yataylogo.svg"
            alt="Mayi Mimarlık"
            style={{ height: '70px', width: 'auto', display: 'block' }}
          />
        </Link>

        {/* Desktop Menu */}
        <nav className="desktop-menu md-hidden h-full flex items-center">
          <ul className="flex items-center gap-lg h-full">
            {navItems.map((item) => {
              if (item.isDropdown) {
                return (
                  <li key={item.name} className="h-full flex items-center nav-dropdown group cursor-pointer">
                    <span className={`nav-link flex items-center gap-1 ${
                      item.children?.some(c => c.path === location.pathname) ? 'active' : ''
                    }`}>
                      {item.name} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform" />
                    </span>
                    <div className="nav-dropdown-content">
                      {item.children?.map(child => (
                        <Link key={child.name} to={child.path} className="nav-dropdown-item">
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </li>
                );
              }

              return (
                <li key={item.name} className="h-full flex items-center">
                  <Link
                    to={item.path!}
                    className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                  >
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Right side controls (Search & Cart & Mobile Menu) */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 mr-2">
            <button 
              className="relative p-2 text-gray-500 hover:text-black transition-colors"
              onClick={() => setIsSearchOpen(true)}
              aria-label="Ürün Ara"
            >
              <Search size={22} />
            </button>
            <button 
              className="relative p-2 text-gray-500 hover:text-black transition-colors"
              onClick={openDrawer}
              aria-label="Teklif Sepeti"
            >
              <ShoppingCart size={22} />
              {count > 0 && (
                <span className="absolute top-0 right-0 w-4 h-4 bg-red-600 text-white flex items-center justify-center rounded-full text-[10px] font-bold shadow-sm">
                  {count}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle Menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>



      {/* Mobile Menu */}
      <div className={`mobile-menu glass ${isOpen ? 'open' : ''}`}>
        <ul className="flex flex-col gap-lg">
          {navItems.map((item) => {
            if (item.isDropdown) {
              return (
                <li key={item.name} className="flex flex-col gap-4">
                  <span className="nav-link text-2xl text-gray-400 cursor-default">{item.name}</span>
                  <div className="flex flex-col gap-4 pl-4 border-l-2 border-gray-100 ml-2">
                    {item.children?.map(child => (
                      <Link
                        key={child.name}
                        to={child.path}
                        className={`nav-link text-xl ${location.pathname === child.path ? 'active text-accent' : ''}`}
                        onClick={() => setIsOpen(false)}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                </li>
              );
            }

            return (
              <li key={item.name}>
                <Link
                  to={item.path!}
                  className={`nav-link text-2xl ${location.pathname === item.path ? 'active text-accent' : ''}`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <GlobalSearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </header>
  );
}
